import { Image } from 'lucide-react';
import type { RefObject } from 'react';
import type { ImageFormat } from '@liks/backend-svc-api/esm/models/index';
import type { ImageAspectRatio } from '../../../types/gallery';
import { LiksTooltip } from '../LiksTooltip/LiksTooltip';
import './LiksChatComposerImageMenu.css';

const IMAGE_FORMAT_OPTIONS: ImageFormat[] = ['PNG', 'JPEG', 'WEBP'];
const IMAGE_ASPECT_RATIO_OPTIONS: ImageAspectRatio[] = [
  '1:1',
  '16:9',
  '9:16',
  '3:4',
];

export type LiksChatComposerImageMenuProps = {
  menuRef: RefObject<HTMLDivElement | null>;
  tooltip: string;
  menuLabel: string;
  toggleLabel: string;
  formatLabel: string;
  formatLabels: Record<ImageFormat, string>;
  aspectRatioLabel: string;
  aspectRatioLabels: Record<ImageAspectRatio, string>;
  imageFormatGroupName: string;
  imageAspectRatioGroupName: string;
  imageGenerationEnabled: boolean;
  imageFormat: ImageFormat;
  imageAspectRatio: ImageAspectRatio;
  isOpen: boolean;
  isLocked: boolean;
  isControlsDisabled: boolean;
  onToggleOpen: () => void;
  onToggleEnabled: () => void;
  onFormatChange: (format: ImageFormat) => void;
  onAspectRatioChange: (aspectRatio: ImageAspectRatio) => void;
};

export function LiksChatComposerImageMenu({
  menuRef,
  tooltip,
  menuLabel,
  toggleLabel,
  formatLabel,
  formatLabels,
  aspectRatioLabel,
  aspectRatioLabels,
  imageFormatGroupName,
  imageAspectRatioGroupName,
  imageGenerationEnabled,
  imageFormat,
  imageAspectRatio,
  isOpen,
  isLocked,
  isControlsDisabled,
  onToggleOpen,
  onToggleEnabled,
  onFormatChange,
  onAspectRatioChange,
}: LiksChatComposerImageMenuProps) {
  return (
    <div className="liks-chat-composer__menu-wrap" ref={menuRef}>
      <LiksTooltip content={tooltip} placement="top-start">
        <button
          type="button"
          className={[
            'liks-chat-composer__attach',
            imageGenerationEnabled && 'liks-chat-composer__attach--active',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={tooltip}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-pressed={imageGenerationEnabled}
          onClick={onToggleOpen}
          disabled={isControlsDisabled || isLocked}
        >
          <Image
            className="liks-chat-composer__attach-icon"
            strokeWidth={2}
            aria-hidden
          />
        </button>
      </LiksTooltip>
      {isOpen && !isLocked && (
        <div
          className="liks-chat-composer__image-menu"
          role="dialog"
          aria-label={menuLabel}
        >
          <label className="liks-chat-composer__image-toggle-row">
            <span>{toggleLabel}</span>
            <input
              type="checkbox"
              checked={imageGenerationEnabled}
              onChange={onToggleEnabled}
              disabled={isLocked || isControlsDisabled}
            />
          </label>

          <fieldset
            className="liks-chat-composer__image-format-group"
            disabled={!imageGenerationEnabled || isControlsDisabled}
          >
            <legend className="liks-chat-composer__image-format-legend">
              {formatLabel}
            </legend>
            <div className="liks-chat-composer__image-format-options">
              {IMAGE_FORMAT_OPTIONS.map((format) => (
                <label
                  key={format}
                  className="liks-chat-composer__image-format-option"
                >
                  <input
                    type="radio"
                    name={imageFormatGroupName}
                    value={format}
                    checked={imageFormat === format}
                    disabled={!imageGenerationEnabled || isControlsDisabled}
                    onChange={() => onFormatChange(format)}
                  />
                  <span>{formatLabels[format]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset
            className="liks-chat-composer__image-format-group"
            disabled={!imageGenerationEnabled || isControlsDisabled}
          >
            <legend className="liks-chat-composer__image-format-legend">
              {aspectRatioLabel}
            </legend>
            <div className="liks-chat-composer__image-format-options">
              {IMAGE_ASPECT_RATIO_OPTIONS.map((aspectRatio) => (
                <label
                  key={aspectRatio}
                  className="liks-chat-composer__image-format-option"
                >
                  <input
                    type="radio"
                    name={imageAspectRatioGroupName}
                    value={aspectRatio}
                    checked={imageAspectRatio === aspectRatio}
                    disabled={!imageGenerationEnabled || isControlsDisabled}
                    onChange={() => onAspectRatioChange(aspectRatio)}
                  />
                  <span>{aspectRatioLabels[aspectRatio]}</span>
                </label>
              ))}
            </div>
          </fieldset>
        </div>
      )}
    </div>
  );
}
